/**
 * connectClient — RPC contract for the callable Revia staff call from the
 * backoffice to connect a managed client automatically, or to renew its
 * licence, through Revia's GitHub App on ReVia-Workspace/Revent.
 *
 * Cross-process contract between the backoffice's Add client and Re-sign
 * dialogs (caller) and Revia's server `connectClient` handler (onCall). Both
 * ends parse with `.strict()`.
 *
 * The caller holds `STAFF_PERMISSION` in `workspaces/revia` and is signed in
 * with a second factor. The server first confirms the App can act on the
 * repository, then signs the licence exactly as `signLicence` does, then
 * runs the GitHub steps in order, stopping at the first refusal:
 *   connect — the environment (`client-<slug>`, from the client's name),
 *             its `v*` tag deployment policy, its variables, its
 *             `LICENCE_TOKEN` secret, and its name in the repository variable
 *             `DEPLOY_ENVIRONMENTS`.
 *   renew   — the licence and the `LICENCE_TOKEN` secret only, into the
 *             environment the licence record names.
 * Every step is idempotent, so a refused run is re-run as is.
 *
 * Input, by `mode`:
 *   connect: client, projectId, wifProvider (GCP_WIF_PROVIDER), serviceAccount
 *            (GCP_SERVICE_ACCOUNT), mainHostname (MAIN_HOSTNAME, optional),
 *            endsAt, graceDays.
 *   renew:   projectId, endsAt, graceDays.
 *
 * Output: the project, its environment, the signed token, and each step by
 * name with its outcome. A failed step carries GitHub's HTTP status and the
 * cause it reads as; the steps after it are skipped. A failed step is an
 * answer, not an error: the licence is signed and the dialog points to the
 * manual pathways for the rest.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   unauthenticated / permission-denied / invalid-argument — as signLicence.
 *   not-found           — renew for a project with no licence.
 *   failed-precondition — `github-not-connected` (no App credentials stored),
 *     `github-not-installed` (the App is not installed on the repository), or
 *     one of `SignLicenceRefusal`; nothing is signed or written.
 */
import { Domain, Licence as SignedLicence } from "@momo/types";
import { z } from "zod";
import { EnvironmentName, LicenceSchema, ProjectId } from "./licence.entity.ts";
import { SignLicenceRefusal } from "./signLicence.action.ts";

const Payload = SignedLicence.LicencePayloadSchema.shape;

// projects/<number>/locations/global/workloadIdentityPools/<pool>/providers/<provider>
export const WifProvider = z
	.string()
	.trim()
	.regex(/^projects\/[0-9]+\/locations\/global\/workloadIdentityPools\/[a-z0-9-]+\/providers\/[a-z0-9-]+$/)
	.describe("the client's Workload Identity Federation provider resource name");
// A service account address in the client's project.
export const ServiceAccount = z
	.string()
	.trim()
	.toLowerCase()
	.regex(/^[a-z][a-z0-9-]{4,28}[a-z0-9]@[a-z][a-z0-9-]{4,28}[a-z0-9]\.iam\.gserviceaccount\.com$/)
	.describe("the client's deploy service account address");

const Licensing = {
	projectId: ProjectId,
	endsAt: Payload.endsAt,
	graceDays: Payload.graceDays.nullish(),
};

export const ConnectClientInput = z.discriminatedUnion("mode", [
	z
		.object({
			mode: z.literal("connect"),
			client: LicenceSchema.shape.client,
			wifProvider: WifProvider,
			serviceAccount: ServiceAccount,
			mainHostname: Domain.HostnameSchema.nullish(),
			...Licensing,
		})
		.strict(),
	z.object({ mode: z.literal("renew"), ...Licensing }).strict(),
]);
export type ConnectClientInput = z.infer<typeof ConnectClientInput>;

/** The steps, in the order they run. */
export const ConnectStep = z.enum(["licence", "environment", "branch-policy", "variables", "secret", "roster"]);
export type ConnectStep = z.infer<typeof ConnectStep>;

/** How a GitHub refusal reads: the App's credentials rejected (401); refused,
 *  by the organization's plan or the App's permissions (403); the repository
 *  or installation not found (404); GitHub declined the request as made (422),
 *  which is also how a plan without private-repository environments answers;
 *  the roster variable holding something other than a JSON array of names;
 *  anything else. */
export const ConnectCause = z.enum(["credentials", "forbidden", "not-found", "rejected", "roster-invalid", "unexpected"]);
export type ConnectCause = z.infer<typeof ConnectCause>;

export const ConnectStepResult = z.discriminatedUnion("outcome", [
	z.object({ step: ConnectStep, outcome: z.literal("done") }).strict(),
	z.object({ step: ConnectStep, outcome: z.literal("skipped") }).strict(),
	z
		.object({
			step: ConnectStep,
			outcome: z.literal("failed"),
			status: z.number().int(),
			cause: ConnectCause,
		})
		.strict(),
]);
export type ConnectStepResult = z.infer<typeof ConnectStepResult>;

export const ConnectClientOutput = z
	.object({
		projectId: ProjectId,
		environment: EnvironmentName,
		token: LicenceSchema.shape.token,
		steps: z.array(ConnectStepResult).min(1),
	})
	.strict();
export type ConnectClientOutput = z.infer<typeof ConnectClientOutput>;

export const ConnectClientRefusal = z.enum([
	"github-not-connected",
	"github-not-installed",
	...SignLicenceRefusal.options,
]);
export type ConnectClientRefusal = z.infer<typeof ConnectClientRefusal>;
